import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lightbulb, FlaskConical, ShieldCheck, Rocket, Accessibility, Heart, Sparkles } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, title: "Privacy First", body: "Privacy is a product value, not a checkbox. We design WYLO to know less than other tools do - by default." },
  { icon: Heart, title: "Employee First", body: "WYLO advocates for the worker. It is never surveillance software, and we will say no to features that violate that line." },
  {
    icon: FlaskConical,
    title: "Evidence-Based",
    body: (
      <>
        We're designing based on what cognitive science and applied research says. You can also{" "}
        <a href="mailto:support@usewylo.com?subject=New%20research%20for%20WYLO" className="text-[#2C6BAC] font-semibold hover:underline" data-testid="about-get-in-touch">
          get in touch
        </a>{" "}
        with new data or studies you think will be helpful!
      </>
    ),
  },
  { icon: Sparkles, title: "Human-Centred AI", body: "AI exists in WYLO to amplify the people using it. Never to replace them, monitor them, or judge them." },
];

const MILESTONES = [
  { icon: Lightbulb, when: "The problem", title: "Where did I leave off?", body: "WYLO began with a question every knowledge worker asks a dozen times a day after a colleague drops by, a ping lands, or a tab wins. The tools we had could silence notifications. None of them could bring us back." },
  { icon: FlaskConical, when: "The research", title: "Reading the science", body: "We went to the studies and found a consistent picture: attention is fragmented every few minutes, recovery is measured in tens of minutes, and in-person interruptions are the least served by software." },
  { icon: ShieldCheck, when: "The principle", title: "Employee-first, or not at all", body: "Every design decision passed one test: would a worker want this on their machine? That ruled out identity-based systems and ruled in derived signals only, like one-way hashing and tier floors to maximise employee privacy." },
  { icon: Rocket, when: "1 November 2026", title: "Launch", body: "WYLO goes live for individuals and teams across the EU and UK, from the free Spark trial to Command for enterprise. Everything is built to GDPR and EU AI Act standards from day one." },
  { icon: Accessibility, when: "1 January 2027", title: "Voice-over in five languages", body: "Full voice-over and screen-reader support arrive in English, German, Spanish, Italian and French - because focus support should not depend on how you read a screen." },
];

export default function OurStory() {
  return (
    <MarketingLayout>
      <section className="pt-28 pb-16 max-w-5xl mx-auto px-6 lg:px-10" data-testid="our-story-hero">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Our Story</p>
        <h1 className="inline-flex flex-col text-[#1A1A2E] text-[4.25rem] sm:text-[6rem] lg:text-[10.5rem]" data-testid="our-story-title">
          <span className="font-soft font-black text-[1em] leading-none tracking-[-0.06em] mr-[0.06em] text-[#1A3A5C]" data-testid="our-story-wylo">WYLO</span>
          <span className="flex justify-center gap-[0.3em] w-0 min-w-full font-display font-medium text-[0.21em] leading-none tracking-tight mt-[0.08em]" aria-label="Where You Left Off" data-testid="our-story-words">
            {["Where", "You", "Left", "Off"].map((w) => <span key={w}>{w}</span>)}
          </span>
        </h1>
        <div className="mt-9 space-y-5 text-lg text-[#555568] leading-relaxed max-w-3xl">
          <p>
            It is the sentence you mutter after the meeting that ran over, the Slack thread that pulled you in, the knock on
            the door that turned into twenty minutes. You come back to your screen and the thread of your own thinking is gone.
          </p>
          <p>
            Back in 2017 on the small island of Barbados, our Founder experienced this challenge daily as an entrepreneur, and
            ideated a system that could minimise the time it took to refocus on a task after legitimate interruptions. A few
            short years later he built WYLO, because he realised that moment - the "what now" of distraction - is where the
            real cost of modern work hides, and it was happening a lot more frequently for workers than anyone was willing to
            admit. Notification blockers try to stop digital interruptions. Calendar tools try to protect the hour. Nobody was
            helping with the return.
          </p>
          <p className="text-[#1A1A2E] font-medium">
            WYLO is the return. A calm prompt that shows you exactly where you were, built on the belief that privacy is immense
            product value when employees are looking for ways to work better, even if their employer pays the bill.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16" data-testid="our-story-why">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">The 'Why'</p>
        <div className="space-y-5 text-lg text-[#555568] leading-relaxed max-w-3xl">
          <p>
            Attention is an entire economy. Every app, every notification, every person in the room is bidding for a share of yours.
            And in modern work you're expected to be everything: focused operator, fast communicator, calendar-juggler,
            in-person presence, decision-maker - sometimes all in the same hour.
          </p>
          <p>
            You can have a busy day and not a productive one. You can be required to show up across a dozen digital surfaces - for work -
            and still try to do the deep thinking that actually moves anything forward. The very tools you use to produce your work are the
            same tools competing to break your concentration.
          </p>
          <p>
            There are products that try to silence digital noise. There is almost nothing for the in-person, real-life
            interruptions that the research says are an even larger driver of cognitive cost - a gaping void in the category.
          </p>
          <p>
            We have twenty-four hours in a day. We feel the pressure to do it all in them. Doing it all in twenty-four hours
            is impossible. That impossibility can become dread.
          </p>
          <p className="text-[#1A1A2E] font-medium">
            It doesn't have to. WYLO is the tool that brings you back.
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-[#E8ECF2]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-10">The journey</p>
          <ol className="relative border-l border-[#E8ECF2] space-y-12">
            {MILESTONES.map((m, i) => (
              <li key={i} className="pl-10 relative" data-testid={`story-milestone-${i}`}>
                <span className="absolute -left-[21px] top-0 h-10 w-10 rounded-full bg-[#1A3A5C] text-white flex items-center justify-center">
                  <m.icon size={16} />
                </span>
                <p className="font-mono text-xs text-[#2C6BAC] pt-2">{m.when}</p>
                <h3 className="font-display text-2xl font-bold text-[#1A1A2E] mt-1">{m.title}</h3>
                <p className="text-[#555568] mt-3 leading-relaxed max-w-2xl">{m.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#F8F9FC]" data-testid="our-story-mission">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Mission</p>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] leading-[1.1]">
              Give people their focus back. Give organisations the insight to support them.
            </h2>
          </div>
          <p className="text-[#555568] leading-relaxed">
            Productivity tools became the wrong fight. We don't want to optimise you. We want to remove the cognitive friction
            that has accumulated in modern knowledge work and quietly cost everyone - workers, teams, and the organisations that depend on them.
          </p>
        </div>
      </section>

      <section id="values" className="scroll-mt-24 bg-white border-y border-[#E8ECF2]" data-testid="our-story-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Values</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-12">
            What we won't compromise on.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <div key={i} className="rounded-xl bg-[#F8F9FC] border border-[#E8ECF2] p-7" data-testid={`about-value-${i}`}>
                <div className="h-11 w-11 rounded-md bg-[#1A3A5C] text-white flex items-center justify-center">
                  <v.icon size={18} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#1A1A2E] mt-5">{v.title}</h3>
                <p className="text-sm text-[#555568] mt-3 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A5C] text-white" data-testid="our-story-ai">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8ECF2]/70 font-semibold mb-4">AI adoption</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tighter leading-[1.1] max-w-3xl">
            A working demonstration that AI can support human work - not replace it.
          </h2>
          <p className="mt-6 text-[#E8ECF2]/80 leading-relaxed max-w-3xl">
            WYLO is one of the few SaaS products built end-to-end with AI tooling that explicitly serves the worker.
            Every model output is grounded in derived signals and metadata.
            That isn't a limitation. It's a design principle.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Why it matters</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] leading-[1.1]">
            A busy day is not the same as a productive one.
          </h2>
        </div>
        <div className="text-[#555568] leading-relaxed space-y-4">
          <p>
            Field studies put the average return from an interruption at over twenty minutes, and the average stretch of
            uninterrupted work at around three. Multiply that by every desk in an organisation and the "hidden tax on focus"
            stops being a metaphor.
          </p>
          <p>
            But the number we care about most is not the money. It is the dread of an impossible day - and the relief of
            getting a piece of it back.
          </p>
          <p data-testid="story-careers-line">
            If what we do matters to you and you'd like to work with us, see our{" "}
            <Link to="/careers" className="text-[#2C6BAC] font-semibold hover:underline" data-testid="story-careers-link">Careers page</Link>{" "}
            to see what's available, or{" "}
            <a href="mailto:jobs@usewylo.com?subject=Working%20with%20WYLO" className="text-[#2C6BAC] font-semibold hover:underline" data-testid="story-jobs-link">get in touch</a>.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/resources#research" data-testid="story-cta-research">
              <Button variant="outline" className="border-[#1A3A5C] text-[#1A1A2E]">
                See the research <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#values" data-testid="story-cta-values">
              <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white">Our values</Button>
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
